import React, { memo, useCallback, useContext } from 'react'
import { Button, Popconfirm } from 'antd'
import { CopyOutlined, DeleteOutlined, DownloadOutlined } from '@ant-design/icons'
import { TimeLineContentWrapper, TimeLineContentHeaderWrapper } from '../../../styles/styles'
import filesize from 'filesize'
import { ActivityFile } from '../../../types/types'
import TimelineItemHeader from '../activity-timeline-item-header/activity-timeline-item-header'
import cls from './activity-timeline-file.module.css'
import { caseDeleteFile } from '../../../../../../store/appeals/actions'
import { TimelineItemProps } from '../activity-timeline-Item'
import { ActivityTimelineContext } from '../../../context/activity-timeline-context'
import ImagePreview from './image-preview/image-preview'
import FilePreview from '../../../../../file-preview'
import { useAppDispatch } from '../../../../../../hooks/useAppDispatch/useAppDispatch'

type ActivityTimelineFileType = Omit<TimelineItemProps, 'data'>
interface ActivityTimelineFileProps extends ActivityTimelineFileType {
  className?: string
  data?: ActivityFile
}

const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp']

const isImageFile = (fileName: string): boolean => {
  const extension = fileName.toLowerCase().slice(fileName.lastIndexOf('.'))
  return imageExtensions.includes(extension)
}

const ActivityTimelineFile: React.FC<ActivityTimelineFileProps> = memo(
  (props: ActivityTimelineFileProps) => {
    const { date, data, index, scopeDate } = props
    const dispatch = useAppDispatch()
    const { deleteTimelineItem, readFiles } = useContext(ActivityTimelineContext)

    const deleteFile = useCallback(
      async (id: number) => {
        await dispatch(caseDeleteFile({ id }))
        deleteTimelineItem?.({ date: scopeDate, index, data: { id } })
      },
      [scopeDate, index, dispatch, deleteTimelineItem],
    )

    return (
      <>
        <TimeLineContentWrapper>
          <TimeLineContentHeaderWrapper>
            <TimelineItemHeader date={date} title={data?.title || 'Файл'} />
          </TimeLineContentHeaderWrapper>
          <div className={cls.fileList}>
            {data?.files?.map((item, index) => {
              const isImage = Boolean(item.name && isImageFile(item.name))
              return (
                <div className={cls.fileRow} key={`${item?.name}${item?.type}${index}`}>
                  {isImage ? (
                    <div className={cls.imageWrapper}>
                      <ImagePreview imageUrl={item.url} alt={item.name} width={100} height={100} />
                    </div>
                  ) : (
                    <a href={item.url} className={cls.fileName} title={item.name}>
                      {item.name || 'Файл'}
                    </a>
                  )}
                  {item?.size !== undefined && item.size > 0 && (
                    <span className={cls.fileSize}>{`(${filesize(item.size)})`}</span>
                  )}
                  <div className={cls.fileActions}>
                    <FilePreview files={item} buttonClassName={cls.button} />
                    {isImage && (
                      <Button
                        className={cls.button}
                        aria-label={`Скачать файл: ${item.name}`}
                        onClick={() => {
                          window.location.href = item.url
                        }}
                      >
                        <DownloadOutlined />
                      </Button>
                    )}
                    <Button
                      className={cls.button}
                      aria-label={`Скопировать ссылку: ${item.name}`}
                      onClick={() => {
                        window?.navigator?.clipboard?.writeText(item.url)
                      }}
                    >
                      <CopyOutlined />
                    </Button>
                    {!readFiles && data?.id !== undefined && (
                      <Popconfirm
                        title="Удалить файл?"
                        onConfirm={() => deleteFile(Number(data.id))}
                      >
                        <Button className={cls.button} aria-label={`Удалить файл: ${item.name}`}>
                          <DeleteOutlined />
                        </Button>
                      </Popconfirm>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </TimeLineContentWrapper>
      </>
    )
  },
)

ActivityTimelineFile.displayName = 'ActivityTimelineFile'
export default ActivityTimelineFile
